/**
 * ValueChainPanel — Conteneur qui orchestre la "rivière" de chaîne de valeur
 * dans l'onglet Origination.
 *
 * V23.15.29 — Page vierge par défaut + bouton "Générer ma chaîne de valeur"
 * (NOAM analyse l'org + Annuaire). Une fois générée :
 *   - rivière horizontale en haut (ValueChainRiver)
 *   - panneau latéral droit : détails du nœud sélectionné (labels humains)
 *   - actions : Régénérer, Ajouter manuellement, Tout effacer
 *
 * Mode additif strict — la collection backend est nouvelle, ne touche pas
 * à workspace_chain_nodes (Position dans la chaîne legacy).
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Sparkles, Plus, RefreshCw, Trash2, X, Loader2, Waves, AlertTriangle, FlaskConical, GitBranch, Check, ArrowRight, TrendingUp, TrendingDown } from "lucide-react";
import ValueChainRiver from "@/components/ValueChainRiver";
import PitchCaptureButton from "@/components/PitchCaptureButton";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import RiverNarrativeBanner from "@/components/RiverNarrativeBanner";
import NodeFrictionMap from "@/components/NodeFrictionMap";

const EMPTY_FORM = {
  name: "",
  role: "",
  flow_position: 30,
  is_self: false,
  capture_pct: 30,
  negotiation_weight_pct: 30,
  notes: "",
  branch: 0,
};

export default function ValueChainPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  // V23.15.31 — What-if mode + scénarios
  const [whatIfMode, setWhatIfMode] = useState(false);
  const [whatIfPreview, setWhatIfPreview] = useState(null);
  const [showScenarios, setShowScenarios] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/workspace/origination/value-chain");
      setData(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Impossible de charger la chaîne de valeur."));
      setData({ nodes: [], is_empty: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/workspace/origination/value-chain/generate");
      setData({ nodes: r.data.nodes || [], is_empty: false });
      // V23.15.30 — Surface les sources utilisées (Positionnement + Annuaire)
      const src = r.data.sources || {};
      const parts = [];
      if (src.positionnement_ecosystem) parts.push("Écosystème Positionnement");
      else if (src.positionnement_profile) parts.push("Profil Positionnement");
      if (src.annuaire_count > 0) parts.push(`Annuaire (${src.annuaire_count})`);
      const suffix = parts.length ? ` · Source : ${parts.join(" + ")}` : "";
      notify.success(`${r.data.generated_count || 0} acteurs identifiés${suffix}.`);
      // Sélectionne le self par défaut
      const self = (r.data.nodes || []).find((n) => n.is_self);
      if (self) setSelectedId(self.id);
    } catch (e) {
      notify.error(fmtError(e, "Génération impossible."));
    } finally {
      setGenerating(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Vider toute la chaîne de valeur ? Cette action est irréversible.")) return;
    try {
      await api.delete("/workspace/origination/value-chain");
      setData({ nodes: [], is_empty: true });
      setSelectedId(null);
      notify.success("Chaîne de valeur réinitialisée.");
    } catch (e) {
      notify.error(fmtError(e, "Suppression impossible."));
    }
  };

  const removeNode = async (id) => {
    if (!window.confirm("Supprimer ce nœud ?")) return;
    try {
      await api.delete(`/workspace/origination/value-chain/nodes/${id}`);
      setData((d) => ({ ...d, nodes: (d?.nodes || []).filter((n) => n.id !== id) }));
      if (selectedId === id) setSelectedId(null);
    } catch (e) {
      notify.error(fmtError(e, "Suppression impossible."));
    }
  };

  const submitAdd = async () => {
    if (!form.name.trim()) {
      notify.error("Nom requis.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/workspace/origination/value-chain/nodes", form);
      setData((d) => ({ ...d, nodes: [...(d?.nodes || []), r.data].sort((a, b) => a.flow_position - b.flow_position), is_empty: false }));
      notify.success("Nœud ajouté.");
      setShowAdd(false);
      setForm(EMPTY_FORM);
    } catch (e) {
      notify.error(fmtError(e, "Ajout impossible."));
    } finally {
      setBusy(false);
    }
  };

  // V23.15.31 — What-if : drag d'un nœud
  const handleMoveNode = useCallback(async (nodeId, newFlowPosition) => {
    try {
      const r = await api.post("/workspace/origination/value-chain/whatif/node", {
        node_id: nodeId,
        new_flow_position: newFlowPosition,
        is_persistent: false,
      });
      setWhatIfPreview(r.data);
      setSelectedId(nodeId);
    } catch (e) {
      notify.error(fmtError(e, "Simulation impossible."));
    }
  }, []);

  // V23.15.31 — Applique le résultat What-if (persiste les nouvelles valeurs)
  const applyWhatIf = async () => {
    if (!whatIfPreview) return;
    try {
      await api.post("/workspace/origination/value-chain/whatif/node", {
        node_id: whatIfPreview.node_id,
        new_flow_position: whatIfPreview.new.flow_position,
        new_branch: whatIfPreview.new.branch,
        is_persistent: true,
      });
      await load();
      setWhatIfPreview(null);
      notify.success("Modification appliquée à la rivière.");
    } catch (e) {
      notify.error(fmtError(e, "Application impossible."));
    }
  };

  if (loading) {
    return (
      <div className="noam-card p-12 text-center" data-testid="value-chain-loading">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-500 mx-auto" />
      </div>
    );
  }

  const nodes = data?.nodes || [];
  const isEmpty = nodes.length === 0;
  const selected = selectedId ? nodes.find((n) => n.id === selectedId) : null;

  // V23.16.10 — Fix snap-back : tant qu'un preview What-if est ouvert, on
  // override la position du nœud déplacé pour qu'il reste visuellement à
  // l'endroit où l'utilisateur l'a lâché (au lieu de revenir à sa position
  // d'origine). Si l'utilisateur annule le preview, displayNodes revient
  // automatiquement à nodes (pas besoin de revert manuel).
  const displayNodes = whatIfPreview
    ? nodes.map((n) =>
        n.id === whatIfPreview.node_id
          ? { ...n, flow_position: whatIfPreview.new.flow_position, branch: whatIfPreview.new.branch ?? n.branch }
          : n,
      )
    : nodes;

  return (
    <div className="space-y-4" data-testid="value-chain-panel" id="value-chain-capture">
      {/* En-tête + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-zinc-100">
          <Waves className="w-4 h-4 text-[#EF9F27]" />
          <h3 className="text-sm font-semibold">Chaîne de valeur · vue rivière</h3>
          {!isEmpty && (
            <span className="text-[10px] text-zinc-500 ml-1">
              · {nodes.length} acteur{nodes.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
        {!isEmpty && (
          <div className="flex items-center gap-2">
            <PitchCaptureButton
              targetId="value-chain-capture"
              filename="noam-chaine-de-valeur"
              docTitle="Chaîne de valeur · vue rivière"
              docSubtitle="Votre position dans le courant de valeur — acteurs amont et aval, flux et points de contrôle."
              threeQEndpoint="/workspace/origination/top-priority"
            />
            <button
              onClick={() => { setWhatIfMode((v) => !v); setWhatIfPreview(null); }}
              className={`text-xs px-2.5 py-1.5 rounded inline-flex items-center gap-1.5 transition ${whatIfMode ? "bg-[#7F77DD]/25 border border-[#7F77DD] text-[#C4BFFF]" : "bg-white/[0.04] border border-white/10 text-zinc-300 hover:bg-white/[0.08]"}`}
              data-testid="value-chain-whatif-toggle"
              title="Activer le mode What-if : glissez un acteur pour simuler une nouvelle position"
            >
              <FlaskConical className="w-3.5 h-3.5" />
              {whatIfMode ? "What-if actif" : "Mode What-if"}
            </button>
            <button
              onClick={() => setShowScenarios(true)}
              className="btn-secondary text-xs inline-flex items-center gap-1.5"
              data-testid="value-chain-scenarios-btn"
            >
              <GitBranch className="w-3.5 h-3.5" /> Scénarios
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="btn-secondary text-xs inline-flex items-center gap-1.5"
              data-testid="value-chain-add-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Ajouter
            </button>
            <button
              onClick={generate}
              disabled={generating}
              className="btn-secondary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="value-chain-regenerate-btn"
              title="Remplace entièrement la rivière par une nouvelle génération NOAM"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Régénérer
            </button>
            <button
              onClick={resetAll}
              className="text-[11px] px-2.5 py-1.5 rounded border border-[#E24B4A]/30 text-[#E24B4A] hover:bg-[#E24B4A]/10 inline-flex items-center gap-1.5"
              data-testid="value-chain-reset-btn"
            >
              <Trash2 className="w-3.5 h-3.5" /> Vider
            </button>
          </div>
        )}
      </div>

      {/* État vide — page vierge avec bouton génération */}
      {isEmpty && (
        <div className="noam-card p-10 text-center" data-testid="value-chain-empty-state">
          <Waves className="w-10 h-10 text-[#EF9F27] mx-auto opacity-70" />
          <h4 className="noam-display text-lg mt-4 text-zinc-100">Visualisez votre chaîne de valeur</h4>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            NOAM analyse votre <strong className="text-zinc-200">module Positionnement</strong> (secteur, zone, écosystème)
            et votre <strong className="text-zinc-200">Annuaire</strong> pour identifier les acteurs en amont
            (régulateurs, infrastructures, plateformes) et en aval (clients, marchés) de votre position.
          </p>
          <button
            onClick={generate}
            disabled={generating}
            className="btn-primary mt-6 mx-auto inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="value-chain-generate-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Générer ma chaîne de valeur
          </button>
          <p className="text-[10px] text-zinc-600 mt-3">
            Génération via NOAM · 10-20 s · 6 à 10 acteurs identifiés
          </p>
        </div>
      )}

      {/* Rivière + panneau détail */}
      {!isEmpty && (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <div className="space-y-3">
            {/* V23.15.60 — Bande de narration doctrinale de la rivière */}
            <RiverNarrativeBanner nodes={nodes} testIdPrefix="value-chain-narrative" />
            <div className="noam-card p-4 overflow-hidden">
              <ValueChainRiver
                nodes={displayNodes}
                selectedId={selectedId}
                onSelectNode={(n) => setSelectedId(n.id)}
                onResetToSelf={() => {
                  const self = nodes.find((n) => n.is_self);
                  if (self) setSelectedId(self.id);
                }}
                onMoveNode={handleMoveNode}
                isWhatIfMode={whatIfMode}
                testIdPrefix="value-chain-river"
              />
              <div className="flex items-center justify-center gap-5 mt-3 text-[10px] text-zinc-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#7F77DD]" /> Amont (fournisseurs, infra)
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[#EF9F27]" /> Vous
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#1D9E75]" /> Aval (clients, marchés)
                </span>
              </div>
            </div>
          </div>

          {/* Panneau détail OU preview What-if */}
          <div className="noam-card p-4 min-h-[280px]" data-testid="value-chain-detail-panel">
            {whatIfPreview ? (
              <WhatIfPreview
                preview={whatIfPreview}
                onApply={applyWhatIf}
                onDiscard={() => setWhatIfPreview(null)}
              />
            ) : !selected ? (
              <div className="text-center text-zinc-500 text-sm py-12">
                {whatIfMode
                  ? "Mode What-if actif. Glissez un acteur pour simuler une nouvelle position."
                  : "Cliquez sur un acteur de la rivière pour voir son détail."}
              </div>
            ) : (
              <NodeDetail node={selected} onRemove={removeNode} />
            )}
          </div>
        </div>
      )}

      {/* Modal ajout manuel */}
      {showAdd && (
        <AddNodeModal
          form={form}
          setForm={setForm}
          onClose={() => { setShowAdd(false); setForm(EMPTY_FORM); }}
          onSubmit={submitAdd}
          busy={busy}
        />
      )}

      {/* V23.15.31 — Drawer Scénarios */}
      {showScenarios && (
        <ScenariosDrawer
          onClose={() => setShowScenarios(false)}
          onApplied={async () => { await load(); setShowScenarios(false); }}
        />
      )}
    </div>
  );
}

function NodeDetail({ node, onRemove }) {
  const isSelf = !!node.is_self;
  const positionLabel = isSelf
    ? "Votre organisation"
    : node.flow_position < 50
    ? `Amont · position ${node.flow_position}/100`
    : `Aval · position ${node.flow_position}/100`;
  return (
    <div className="space-y-3" data-testid={`value-chain-node-detail-${node.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{positionLabel}</div>
          <h4 className="text-base font-semibold text-zinc-100 mt-0.5 break-words">
            {isSelf && <span className="text-[#EF9F27] mr-1">★</span>}
            {node.name}
          </h4>
          {node.role && <div className="text-xs text-zinc-400 mt-0.5">{node.role}</div>}
        </div>
        {!isSelf && (
          <button
            onClick={() => onRemove(node.id)}
            className="p-1 rounded hover:bg-[#E24B4A]/15 text-[#E24B4A]"
            title="Supprimer ce nœud"
            data-testid={`value-chain-detail-remove-${node.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Métriques avec labels humains */}
      <div className="space-y-2.5 pt-2 border-t border-white/5">
        <MetricBar
          label="Valeur que vous capturez"
          value={node.capture_pct}
          color="#1D9E75"
          help="Part de la valeur économique de cette étape qui revient à cet acteur."
        />
        <MetricBar
          label="Poids dans la négociation"
          value={node.negotiation_weight_pct}
          color="#7F77DD"
          help="Influence de cet acteur sur les conditions commerciales."
        />
      </div>

      {node.notes && (
        <div className="pt-2 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Notes</div>
          <p className="text-xs text-zinc-300 leading-relaxed">{node.notes}</p>
        </div>
      )}

      {node.source && (
        <div className="text-[10px] text-zinc-600 pt-1">
          Source : {
            node.source === "positionnement" ? "Module Positionnement (acteur réel)" :
            node.source === "annuaire" ? "Annuaire" :
            node.source === "claude" ? "Génération NOAM" :
            "Ajout manuel"
          }
        </div>
      )}

      {/* V23.15.61 — Friction map contextuelle au niveau causal du nœud */}
      <NodeFrictionMap nodeId={node.id} testIdPrefix={`value-chain-friction-${node.id}`} />
    </div>
  );
}

function MetricBar({ label, value, color, help }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-zinc-300" title={help}>{label}</span>
        <span className="text-[11px] font-medium text-zinc-100">{pct}%</span>
      </div>
      <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function AddNodeModal({ form, setForm, onClose, onSubmit, busy }) {
  // V23.15.30 — Escape ferme la modale (a11y)
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="value-chain-add-modal">
      <div className="noam-card max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-zinc-100">Ajouter un acteur à la rivière</h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="value-chain-add-close">
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom de l&apos;acteur</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-[#7F77DD]"
              placeholder="FINMA, SWIFT, banque privée X…"
              data-testid="value-chain-add-name"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Rôle (court)</label>
            <input
              type="text"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mt-1 w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-[#7F77DD]"
              placeholder="Régulateur, plateforme tech, client final…"
              data-testid="value-chain-add-role"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Position dans le flux</label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={form.flow_position}
                  onChange={(e) => setForm({ ...form, flow_position: Number(e.target.value) })}
                  className="flex-1"
                  data-testid="value-chain-add-flow"
                />
                <span className="text-xs text-zinc-300 w-8 text-right">{form.flow_position}</span>
              </div>
              <div className="text-[9px] text-zinc-600 mt-0.5">0 = amont extrême · 50 = vous · 100 = aval extrême</div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Branche</label>
              <select
                value={form.branch}
                onChange={(e) => setForm({ ...form, branch: Number(e.target.value) })}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-[#7F77DD]"
                data-testid="value-chain-add-branch"
              >
                <option value={0}>Principale (centre)</option>
                <option value={1}>Haute</option>
                <option value={2}>Basse</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500" title="Part de la valeur économique captée par cet acteur.">
                Valeur capturée (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={form.capture_pct}
                onChange={(e) => setForm({ ...form, capture_pct: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-[#7F77DD]"
                data-testid="value-chain-add-capture"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500" title="Influence de cet acteur sur les conditions commerciales.">
                Poids négociation (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={form.negotiation_weight_pct}
                onChange={(e) => setForm({ ...form, negotiation_weight_pct: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-[#7F77DD]"
                data-testid="value-chain-add-weight"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input
              type="checkbox"
              checked={form.is_self}
              onChange={(e) => setForm({ ...form, is_self: e.target.checked, flow_position: e.target.checked ? 50 : form.flow_position })}
              data-testid="value-chain-add-is-self"
            />
            C&apos;est mon organisation (positionné au centre)
          </label>
          {form.is_self && (
            <div className="text-[10px] text-amber-400 inline-flex items-center gap-1.5">
              <AlertTriangle className="w-3 h-3" />
              S&apos;il existe déjà un nœud &laquo;&nbsp;vous&nbsp;&raquo;, il sera remplacé par celui-ci.
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} className="btn-secondary text-xs" data-testid="value-chain-add-cancel">Annuler</button>
          <button
            onClick={onSubmit}
            disabled={busy || !form.name.trim()}
            className="btn-primary text-xs disabled:opacity-50"
            data-testid="value-chain-add-submit"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// V23.15.31 — Composants What-if + Scénarios
// ============================================================================

function DeltaBadge({ label, oldVal, newVal, unit = "%" }) {
  const delta = (Number(newVal) || 0) - (Number(oldVal) || 0);
  const positive = delta > 0;
  const negative = delta < 0;
  const Icon = positive ? TrendingUp : negative ? TrendingDown : ArrowRight;
  const color = positive ? "text-emerald-400" : negative ? "text-[#E24B4A]" : "text-zinc-400";
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-zinc-400">{label}</span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-zinc-500">{oldVal}{unit}</span>
        <ArrowRight className="w-3 h-3 text-zinc-600" />
        <span className="font-medium text-zinc-100">{newVal}{unit}</span>
        <span className={`inline-flex items-center gap-0.5 text-[10px] ${color}`}>
          <Icon className="w-3 h-3" />
          {delta > 0 ? "+" : ""}{delta.toFixed(1)}
        </span>
      </span>
    </div>
  );
}

function WhatIfPreview({ preview, onApply, onDiscard }) {
  if (!preview) return null;
  return (
    <div className="space-y-3" data-testid="value-chain-whatif-preview">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-[#7F77DD]">
          <FlaskConical className="w-3.5 h-3.5" />
          <span className="text-[10px] uppercase tracking-wider font-semibold">Simulation What-if</span>
        </div>
        <button onClick={onDiscard} className="p-1 hover:bg-white/10 rounded" data-testid="value-chain-whatif-discard">
          <X className="w-3.5 h-3.5 text-zinc-500" />
        </button>
      </div>
      <h4 className="text-base font-semibold text-zinc-100 break-words">{preview.node_name}</h4>

      {/* V23.15.57 — Brique 3 : score de confiance + explicabilité */}
      {typeof preview.confidence === "number" && (
        <div className="pt-1">
          <ConfidenceBadge
            value={preview.confidence}
            rationale={preview.justification}
            assumptions={preview.assumptions || []}
            caveats={preview.caveats || []}
          />
        </div>
      )}

      <div className="space-y-2 pt-2 border-t border-white/5">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Cet acteur</div>
        <DeltaBadge label="Position" oldVal={preview.old.flow_position} newVal={preview.new.flow_position} unit="" />
        <DeltaBadge label="Valeur capturée" oldVal={preview.old.capture_pct} newVal={preview.new.capture_pct} />
        <DeltaBadge label="Poids négociation" oldVal={preview.old.negotiation_weight_pct} newVal={preview.new.negotiation_weight_pct} />
      </div>

      {preview.self_impact && (
        <div className="space-y-2 pt-2 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 inline-flex items-center gap-1.5">
            <span className="text-[#EF9F27]">★</span> Impact sur votre organisation
          </div>
          <DeltaBadge label="Votre valeur capturée" oldVal={preview.self_impact.old_capture_pct} newVal={preview.self_impact.new_capture_pct} />
          <DeltaBadge label="Votre poids négociation" oldVal={preview.self_impact.old_negotiation_weight_pct} newVal={preview.self_impact.new_negotiation_weight_pct} />
        </div>
      )}

      {preview.justification && (
        <div className="pt-2 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Justification</div>
          <p className="text-xs text-zinc-300 leading-relaxed">{preview.justification}</p>
        </div>
      )}

      {/* V23.15.57 — Nouvelle information générée par la simulation */}
      {Array.isArray(preview.market_impact) && preview.market_impact.length > 0 && (
        <div className="pt-2 border-t border-white/5" data-testid="whatif-market-impact">
          <div className="text-[10px] uppercase tracking-wider text-[#378ADD] mb-1.5 font-semibold">Impact sur le marché</div>
          <ul className="space-y-1">
            {preview.market_impact.map((line, i) => (
              <li key={i} className="text-[11px] text-zinc-300 leading-snug flex gap-1.5">
                <span className="text-[#378ADD] flex-shrink-0">•</span><span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(preview.new_opportunities) && preview.new_opportunities.length > 0 && (
        <div className="pt-2 border-t border-white/5" data-testid="whatif-new-opps">
          <div className="text-[10px] uppercase tracking-wider text-[#1D9E75] mb-1.5 font-semibold">Nouvelles opportunités qui émergent</div>
          <ul className="space-y-1">
            {preview.new_opportunities.map((line, i) => (
              <li key={i} className="text-[11px] text-zinc-300 leading-snug flex gap-1.5">
                <span className="text-[#1D9E75] flex-shrink-0">+</span><span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(preview.new_risks) && preview.new_risks.length > 0 && (
        <div className="pt-2 border-t border-white/5" data-testid="whatif-new-risks">
          <div className="text-[10px] uppercase tracking-wider text-[#E24B4A] mb-1.5 font-semibold">Nouveaux risques créés</div>
          <ul className="space-y-1">
            {preview.new_risks.map((line, i) => (
              <li key={i} className="text-[11px] text-zinc-300 leading-snug flex gap-1.5">
                <span className="text-[#E24B4A] flex-shrink-0">!</span><span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(preview.cascade) && preview.cascade.length > 0 && (
        <div className="pt-2 border-t border-white/5" data-testid="whatif-cascade">
          <div className="text-[10px] uppercase tracking-wider text-[#7F77DD] mb-1.5 font-semibold">Cascade sur les voisins</div>
          <ul className="space-y-1.5">
            {preview.cascade.map((c, i) => (
              <li key={i} className="text-[11px] leading-snug">
                <div className="text-zinc-200 font-semibold">{c.actor}</div>
                <div className="text-zinc-400">{c.movement}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview.transition_cost?.level && (
        <div className="pt-2 border-t border-white/5" data-testid="whatif-transition-cost">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 font-semibold">Coût de la transition</div>
          <div className="flex items-center gap-2">
            {(() => {
              const lvl = preview.transition_cost.level;
              const cfg = {
                faible: { color: "text-[#1D9E75]", bg: "bg-[#1D9E75]/10 border-[#1D9E75]/30", label: "Faible" },
                moyen:  { color: "text-[#EF9F27]", bg: "bg-[#EF9F27]/10 border-[#EF9F27]/30", label: "Moyen" },
                eleve:  { color: "text-[#E24B4A]", bg: "bg-[#E24B4A]/10 border-[#E24B4A]/30", label: "Élevé" },
              }[lvl] || { color: "text-zinc-400", bg: "bg-white/[0.04] border-white/10", label: lvl };
              return (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${cfg.color} ${cfg.bg}`}>{cfg.label}</span>
              );
            })()}
          </div>
          {preview.transition_cost.explanation && (
            <p className="text-[11px] text-zinc-400 mt-1.5 leading-snug">{preview.transition_cost.explanation}</p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={onApply}
          className="btn-primary text-xs flex-1 inline-flex items-center justify-center gap-1.5"
          data-testid="value-chain-whatif-apply"
        >
          <Check className="w-3.5 h-3.5" /> Appliquer
        </button>
        <button
          onClick={onDiscard}
          className="btn-secondary text-xs"
          data-testid="value-chain-whatif-cancel"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}

function ScenariosDrawer({ onClose, onApplied }) {
  const [scenarios, setScenarios] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [scenarioForm, setScenarioForm] = useState({ name: "", description: "" });
  const [applying, setApplying] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.get("/workspace/origination/value-chain/scenarios");
      setScenarios(r.data.scenarios || []);
    } catch (e) {
      notify.error(fmtError(e, "Chargement scénarios impossible."));
      setScenarios([]);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const createScenario = async () => {
    if (!scenarioForm.name.trim() || scenarioForm.description.trim().length < 5) {
      notify.error("Nom + description (5 caractères min) requis.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/workspace/origination/value-chain/scenarios", scenarioForm);
      notify.success("Scénario projeté par NOAM.");
      setScenarioForm({ name: "", description: "" });
      setShowCreate(false);
      await reload();
    } catch (e) {
      notify.error(fmtError(e, "Génération du scénario impossible."));
    } finally {
      setCreating(false);
    }
  };

  const deleteScenario = async (id) => {
    if (!window.confirm("Supprimer ce scénario ?")) return;
    try {
      await api.delete(`/workspace/origination/value-chain/scenarios/${id}`);
      await reload();
    } catch (e) {
      notify.error(fmtError(e, "Suppression impossible."));
    }
  };

  const applyScenario = async (id) => {
    if (!window.confirm("Appliquer ce scénario remplacera la rivière actuelle. Confirmer ?")) return;
    setApplying(id);
    try {
      await api.post(`/workspace/origination/value-chain/scenarios/${id}/apply`);
      notify.success("Scénario appliqué : rivière mise à jour.");
      await onApplied();
    } catch (e) {
      notify.error(fmtError(e, "Application impossible."));
    } finally {
      setApplying(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="value-chain-scenarios-drawer">
      <div className="w-full max-w-xl bg-[#0F0F11] border-l border-white/10 h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0F0F11] border-b border-white/10 px-5 py-4 flex items-center justify-between z-10">
          <div className="inline-flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-[#7F77DD]" />
            <h3 className="text-base font-semibold text-zinc-100">Scénarios stratégiques</h3>
          </div>
          <div className="inline-flex items-center gap-2">
            <button
              onClick={() => setShowCreate((v) => !v)}
              className="btn-primary text-xs inline-flex items-center gap-1.5"
              data-testid="value-chain-scenarios-create-toggle"
            >
              <Plus className="w-3.5 h-3.5" /> {showCreate ? "Annuler" : "Nouveau"}
            </button>
            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="value-chain-scenarios-close">
              <X className="w-4 h-4 text-zinc-400" />
            </button>
          </div>
        </div>

        {showCreate && (
          <div className="px-5 py-4 border-b border-white/10 space-y-3 bg-white/[0.02]" data-testid="value-chain-scenario-create-form">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom du scénario</label>
              <input
                type="text"
                value={scenarioForm.name}
                onChange={(e) => setScenarioForm({ ...scenarioForm, name: e.target.value })}
                className="mt-1 w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-[#7F77DD]"
                placeholder="Ex : Acquisition d'Avaloq, Sortie du marché EU, Nouvelle régulation FINMA"
                data-testid="value-chain-scenario-name"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Description (contexte stratégique)</label>
              <textarea
                value={scenarioForm.description}
                onChange={(e) => setScenarioForm({ ...scenarioForm, description: e.target.value })}
                rows="4"
                className="mt-1 w-full bg-black/40 border border-white/10 rounded px-2.5 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-[#7F77DD]"
                placeholder="Décrivez la situation projetée : acteurs, dynamiques, impacts attendus…"
                data-testid="value-chain-scenario-description"
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={createScenario}
                disabled={creating || !scenarioForm.name.trim() || scenarioForm.description.trim().length < 5}
                className="btn-primary text-xs disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="value-chain-scenario-submit"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Projeter avec NOAM
              </button>
            </div>
            <p className="text-[10px] text-zinc-600">
              NOAM projette une nouvelle rivière + estime l&apos;impact sur la valeur brute/nette d&apos;Origination. 10-25 s.
            </p>
          </div>
        )}

        <div className="px-5 py-4 space-y-3">
          {scenarios === null ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-500 mx-auto" /></div>
          ) : scenarios.length === 0 ? (
            <div className="text-center text-zinc-500 text-sm py-12" data-testid="value-chain-scenarios-empty">
              Aucun scénario projeté pour l&apos;instant.<br />
              <span className="text-[11px]">Créez-en un pour simuler des trajectoires stratégiques.</span>
            </div>
          ) : scenarios.map((sc) => {
            const dBrute = sc.valuation_impact?.delta_brute_mEUR || 0;
            const dNette = sc.valuation_impact?.delta_nette_mEUR || 0;
            const isExpanded = expandedId === sc.id;
            return (
              <div
                key={sc.id}
                className="noam-card p-3"
                data-testid={`value-chain-scenario-${sc.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-semibold text-zinc-100 break-words">{sc.name}</h4>
                    <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{sc.description}</p>
                  </div>
                  <button
                    onClick={() => deleteScenario(sc.id)}
                    className="p-1 rounded hover:bg-[#E24B4A]/15 text-[#E24B4A] flex-shrink-0"
                    title="Supprimer"
                    data-testid={`value-chain-scenario-delete-${sc.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-white/5">
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">Δ Brute</div>
                    <div className={`text-base font-semibold ${dBrute > 0 ? "text-emerald-400" : dBrute < 0 ? "text-[#E24B4A]" : "text-zinc-300"}`}>
                      {dBrute > 0 ? "+" : ""}{dBrute.toFixed(1)} M€
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">Δ Nette</div>
                    <div className={`text-base font-semibold ${dNette > 0 ? "text-emerald-400" : dNette < 0 ? "text-[#E24B4A]" : "text-zinc-300"}`}>
                      {dNette > 0 ? "+" : ""}{dNette.toFixed(1)} M€
                    </div>
                  </div>
                </div>

                {sc.justification && (
                  <button
                    onClick={() => setExpandedId((cur) => cur === sc.id ? null : sc.id)}
                    className="text-[10px] text-[#7F77DD] hover:text-[#9F95FF] mt-2"
                    data-testid={`value-chain-scenario-toggle-${sc.id}`}
                  >
                    {isExpanded ? "Masquer" : "Voir"} la justification stratégique
                  </button>
                )}
                {isExpanded && sc.justification && (
                  <p className="text-xs text-zinc-300 mt-2 leading-relaxed border-t border-white/5 pt-2">
                    {sc.justification}
                  </p>
                )}

                <div className="flex items-center justify-end gap-2 mt-3 pt-2 border-t border-white/5">
                  <button
                    onClick={() => applyScenario(sc.id)}
                    disabled={applying === sc.id}
                    className="text-xs px-3 py-1.5 rounded bg-[#EF9F27]/15 border border-[#EF9F27]/40 text-[#EF9F27] hover:bg-[#EF9F27]/25 disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`value-chain-scenario-apply-${sc.id}`}
                  >
                    {applying === sc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Appliquer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

